import { isNumber } from 'lodash';
import { getFramework } from '@/framework';
import { createCanvas, loadImageAsync } from '@/utils/file-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import { rotatePoint } from '@/utils/math-kit';
import { serializeSVG } from '@/utils/svg-kit';
import { transformClientVectorLayerDataToFoundation } from '@/utils/transform-kit';
import { pxToInch } from '@/utils/unit-kit';
import { type BoundingBox, type ILayerBehavior, type ILayerProps, type IVectorLayerProps } from '@/version-types';
import { EdgelessVectorLayer } from '../edgeless';

type Behaviors = Pick<ILayerBehavior, 'onBeforeSaveAsync' | 'savePPTLayerAsync' | 'getResultCanvasAsync'>;

/**
 * 等待 Vector 完整文字输出后，将导出 SVG 结果栅格化为局部 Canvas。
 *
 * 栅格化与舞台合成属于保存行为的职责，因此从 Edgeless 对象取出组合完成的 SVG 结果后在此落地为
 * Canvas；对象只负责交付 SVG 与 Render Box。
 *
 * @param object - 目标 Vector Edgeless 对象。
 * @returns 局部 Canvas 与其组合 Render Box；无法构建时返回 null。
 */
const rasterizeExportResult = async (
    object: EdgelessVectorLayer,
): Promise<{ canvas: HTMLCanvasElement; renderBox: BoundingBox } | null> => {
    const result = await object.resolveExportSVGResult();
    if (!result) return null;

    const image = await loadImageAsync(serializeSVG(result.svg).dataURL);
    const { canvas, ctx } = createCanvas(Math.ceil(result.renderBox.width), Math.ceil(result.renderBox.height));
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    return { canvas, renderBox: result.renderBox };
};

/**
 * 创建 Vector Layer 的保存与导出行为。
 *
 * @param identity - 当前编辑器实例标识。
 * @returns Vector Layer 保存行为集合。
 */
export const createSaveBehaviors = (identity: string) => {
    const behaviors: Behaviors = {
        /**
         * 保存前将客户端矢量数据转换为底层格式。
         *
         * @param layer - 当前矢量图层。
         * @param isAutoSave - 是否自动保存。
         * @returns 完成数据转换后结束的 Promise。
         */
        async onBeforeSaveAsync(layer: ILayerProps, _isAutoSave: boolean): Promise<void> {
            const data = layer.data as IVectorLayerProps;
            deleteExtraInfo(layer);
            data.pickerIndex = isNumber(data.pickerIndex) ? data.pickerIndex : null;

            layer.data = transformClientVectorLayerDataToFoundation(data);
        },

        /**
         * 将矢量图层以统一 Edgeless 结果写入 PPT。
         *
         * @param slide - 目标 PPT 页面。
         * @param layer - 当前矢量图层。
         * @param stageWidth - 舞台宽度，本地图片定位已包含舞台坐标所以无需额外使用。
         * @param stageHeight - 舞台高度，本地图片定位已包含舞台坐标所以无需额外使用。
         * @param watermark - PPT 水印参数，本阶段保持 Vector 现有忽略语义。
         * @param outFontFamilyArr - 外部字体列表，Vector 图形主体不消费该参数。
         * @returns 图片写入完成后结束的 Promise。
         */
        async savePPTLayerAsync(slide, layer, _stageWidth, _stageHeight, _watermark, _outFontFamilyArr): Promise<void> {
            const object = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id);
            if (!(object instanceof EdgelessVectorLayer)) return;

            const result = await rasterizeExportResult(object);
            if (!result) return;

            let { canvas } = result;
            const { renderBox } = result;

            // PPT 绕导入图片自身中心旋转，编辑器则绕原图层中心旋转。特效产生非对称
            // 外扩后两个中心不再重合，因此需要先把 Render Box 中心相对图层中心的偏移
            // 旋转到舞台坐标，再以校正后的中心定位 PPT 图片，避免阴影等特效导致旋转后漂移。
            const localCenterOffset = {
                x: renderBox.left + renderBox.width / 2 - layer.width / 2,
                y: renderBox.top + renderBox.height / 2 - layer.height / 2,
            };
            const rotatedOffset = rotatePoint(localCenterOffset, layer.rotate);
            const centerX = layer.left + layer.width / 2 + rotatedOffset.x;
            const centerY = layer.top + layer.height / 2 + rotatedOffset.y;
            const imageBox = {
                left: centerX - renderBox.width / 2,
                top: centerY - renderBox.height / 2,
                width: renderBox.width,
                height: renderBox.height,
            };

            // Edgeless 局部 Canvas 不包含图层外层透明度，PPT 又无法在 addImage 时单独设置透明度。
            // 只在 opacity 小于 1 时创建已合成透明度的 Canvas；不透明时继续复用 Edgeless 结果，
            // 避免额外分配 Canvas 和重复绘制。
            if (layer.opacity < 1) {
                const opacityCanvas = createCanvas(canvas.width, canvas.height);
                opacityCanvas.ctx.globalAlpha = Math.max(0, layer.opacity);
                opacityCanvas.ctx.drawImage(canvas, 0, 0);
                canvas = opacityCanvas.canvas;
            }

            slide.addImage({
                x: pxToInch(imageBox.left),
                y: pxToInch(imageBox.top),
                w: pxToInch(imageBox.width),
                h: pxToInch(imageBox.height),
                rotate: layer.rotate > 0 ? layer.rotate : 360 + layer.rotate,
                data: canvas.toDataURL(),
            });
        },

        /**
         * 将矢量图层合成到舞台尺寸 Canvas。
         *
         * @param layer - 当前矢量图层。
         * @param stageSize - 目标舞台尺寸。
         * @returns 舞台 Canvas；Edgeless 结果不可用时返回 undefined。
         */
        async getResultCanvasAsync(layer, stageSize): Promise<HTMLCanvasElement | undefined> {
            const object = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id);
            if (!(object instanceof EdgelessVectorLayer)) return undefined;

            const result = await rasterizeExportResult(object);
            if (!result) return undefined;

            const { canvas: layerCanvas, renderBox } = result;
            const { canvas, ctx } = createCanvas(stageSize.width, stageSize.height);

            // 局部 Canvas 的原点位于 Render Box 左上角，而舞台旋转中心是原图层中心。先把
            // 坐标系移到图层中心并应用旋转，再使用 Render Box 相对图层中心的偏移绘制，
            // 才能同时保持图层旋转轴和特效外扩位置。
            ctx.translate(layer.left + layer.width / 2, layer.top + layer.height / 2);
            ctx.rotate((layer.rotate * Math.PI) / 180);
            ctx.globalAlpha = layer.opacity;
            ctx.drawImage(
                layerCanvas,
                renderBox.left - layer.width / 2,
                renderBox.top - layer.height / 2,
                renderBox.width,
                renderBox.height,
            );

            return canvas;
        },
    };

    return behaviors;
};
